import { useState, useRef, useEffect } from 'react';
import { apiFetch, API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  MessageSquare, Send, X, Loader2, Clipboard, Check,
  Trash2, ListTodo, Bot, ChevronDown
} from 'lucide-react';

const AIAssistant = () => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('chat'); // chat | todos
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hey! I'm your File Room AI assistant. I can help you manage the schedule, sponsors, store, tickets, and more — just tell me what you need in plain English.\n\nFor things that need a developer (design changes, new features, bug fixes), I'll add them to the Developer Requests list." }
  ]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [todos, setTodos] = useState([]);
  const [copied, setCopied] = useState(false);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && tab === 'todos') fetchTodos();
  }, [open, tab]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchTodos = async () => {
    try {
      const res = await apiFetch(`${API_BASE}/assistant/todos`);
      if (res.ok) setTodos(await res.json());
    } catch {}
  };

  const deleteTodo = async (id) => {
    await apiFetch(`${API_BASE}/assistant/todos/${id}`, { method: 'DELETE' });
    setTodos(prev => prev.filter(t => t.id !== id));
  };

  const toggleTodo = async (todo) => {
    const newStatus = todo.status === 'done' ? 'pending' : 'done';
    await apiFetch(`${API_BASE}/assistant/todos/${todo.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: newStatus }),
    });
    setTodos(prev => prev.map(t => t.id === todo.id ? { ...t, status: newStatus } : t));
  };

  const copyTodos = () => {
    const pending = todos.filter(t => t.status === 'pending');
    const text = pending.map((t, i) => `${i + 1}. [${t.priority.toUpperCase()}] ${t.description}`).join('\n');
    navigator.clipboard.writeText(text || 'No pending requests');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sendMessage = async () => {
    if (!input.trim() || streaming) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setStreaming(true);

    // Add empty assistant message to stream into
    setMessages(prev => [...prev, { role: 'assistant', content: '', streaming: true }]);

    try {
      const token = localStorage.getItem('elevatemn_session');
      const res = await fetch(`${API_BASE}/assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message: userMsg }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'assistant', content: err.detail || 'Something went wrong.' };
          return updated;
        });
        setStreaming(false);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);

          if (data === '[DONE]') continue;
          if (data.startsWith('[ACTION_RESULT]')) {
            // Action was executed — refresh todos
            fetchTodos();
            continue;
          }

          fullText += data;
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { role: 'assistant', content: fullText };
            return updated;
          });
        }
      }

      // Clean up action blocks from display
      setMessages(prev => {
        const updated = [...prev];
        let content = updated[updated.length - 1].content;
        content = content.replace(/```action[\s\S]*?```/g, '').trim();
        updated[updated.length - 1] = { role: 'assistant', content };
        return updated;
      });

    } catch (err) {
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: 'assistant', content: 'Connection error. Please try again.' };
        return updated;
      });
    }

    setStreaming(false);
    fetchTodos();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const pendingCount = todos.filter(t => t.status === 'pending').length;

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 bg-brand-gold text-brand-dark p-3.5 shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center gap-2 font-semibold text-sm"
        data-testid="ai-assistant-toggle"
      >
        {open ? <X className="w-5 h-5" /> : <Bot className="w-5 h-5" />}
        {!open && 'AI Assistant'}
        {!open && pendingCount > 0 && (
          <span className="bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">{pendingCount}</span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-20 right-6 z-50 w-96 h-[520px] bg-white border shadow-2xl flex flex-col" data-testid="ai-assistant-panel">
          {/* Header */}
          <div className="bg-brand-dark p-3 flex items-center justify-between">
            <div className="flex gap-1">
              <button onClick={() => setTab('chat')} className={`px-3 py-1.5 text-xs font-medium rounded-sm ${tab === 'chat' ? 'bg-brand-gold text-brand-dark' : 'text-gray-300 hover:text-white'}`} data-testid="ai-tab-chat">
                <MessageSquare className="w-3.5 h-3.5 inline mr-1" />Chat
              </button>
              <button onClick={() => setTab('todos')} className={`px-3 py-1.5 text-xs font-medium rounded-sm flex items-center gap-1 ${tab === 'todos' ? 'bg-brand-gold text-brand-dark' : 'text-gray-300 hover:text-white'}`} data-testid="ai-tab-todos">
                <ListTodo className="w-3.5 h-3.5" />Dev Requests
                {pendingCount > 0 && <span className="bg-red-500 text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full">{pendingCount}</span>}
              </button>
            </div>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white"><ChevronDown className="w-4 h-4" /></button>
          </div>

          {tab === 'chat' ? (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap ${
                      msg.role === 'user'
                        ? 'bg-brand-dark text-white rounded-l-lg rounded-tr-lg'
                        : 'bg-gray-100 text-gray-800 rounded-r-lg rounded-tl-lg'
                    }`}>
                      {msg.content || (msg.streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : '')}
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>

              {/* Input */}
              <div className="border-t p-3 flex gap-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask me anything..."
                  className="flex-1 text-sm h-9"
                  disabled={streaming}
                  data-testid="ai-chat-input"
                />
                <Button size="sm" onClick={sendMessage} disabled={streaming || !input.trim()} className="h-9 px-3" data-testid="ai-send-btn">
                  {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </>
          ) : (
            /* Todos / Dev Requests */
            <div className="flex-1 overflow-y-auto">
              <div className="p-3 border-b flex items-center justify-between">
                <p className="text-sm font-medium text-brand-dark">{pendingCount} pending request{pendingCount !== 1 ? 's' : ''}</p>
                <Button variant="outline" size="sm" onClick={copyTodos} className="h-7 text-xs" data-testid="copy-todos-btn">
                  {copied ? <><Check className="w-3 h-3 mr-1" />Copied!</> : <><Clipboard className="w-3 h-3 mr-1" />Copy All</>}
                </Button>
              </div>
              <div className="p-3 space-y-2">
                {todos.length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-6">No developer requests yet. Ask me for something that needs a code change!</p>
                ) : (
                  todos.map(todo => (
                    <div key={todo.id} className={`flex items-start gap-2 p-2 border text-sm ${todo.status === 'done' ? 'opacity-50' : ''}`} data-testid={`todo-${todo.id}`}>
                      <input type="checkbox" checked={todo.status === 'done'} onChange={() => toggleTodo(todo)} className="mt-1 accent-brand-gold" />
                      <div className="flex-1 min-w-0">
                        <p className={`${todo.status === 'done' ? 'line-through text-gray-400' : 'text-brand-dark'}`}>{todo.description}</p>
                        <span className={`text-[10px] px-1.5 py-0.5 uppercase ${
                          todo.priority === 'high' ? 'bg-red-100 text-red-600' :
                          todo.priority === 'low' ? 'bg-gray-100 text-gray-500' :
                          'bg-blue-100 text-blue-600'
                        }`}>{todo.priority}</span>
                      </div>
                      <button onClick={() => deleteTodo(todo.id)} className="p-1 hover:bg-gray-100 rounded"><Trash2 className="w-3.5 h-3.5 text-gray-400" /></button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AIAssistant;
